import { ChangeEvent, FC, FocusEvent, forwardRef, MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import block from 'bem-cn'

import { startOfDay } from 'shared/utils/date/getPeriod'

import { useDatePickerContext } from 'components/UIKit/DatePicker/DatePickerProvider'

import './InputDate.scss'

type InputDateProps = {}

const b = block('input-date')

export const InputDate = forwardRef<HTMLInputElement, InputDateProps>(({}, ref) => {
  const { value: dateValue, selectDate, onError, modalControls } = useDatePickerContext()
  const [isDayFocused, setIsDayFocused] = useState<boolean>(false)
  const [isMonthFocused, setIsMonthFocused] = useState<boolean>(false)
  const [isYearFocused, setIsYearFocused] = useState<boolean>(false)

  const [day, setDay] = useState<string>(() => {
    const days = (dateValue as Date | null)?.getDate()

    if (days) {
      return days > 9 ? String(days) : `0${days}`
    }

    return ''
  })
  const [month, setMonth] = useState<string>(() => {
    const months = (dateValue as Date | null)?.getMonth()

    if (months) {
      return months + 1 > 9 ? String(months + 1) : `0${months + 1}`
    }

    return ''
  })
  const [year, setYear] = useState<string>(() => {
    const years = (dateValue as Date | null)?.getFullYear()

    return String(years) || ''
  })

  const monthInputRef = useRef<HTMLInputElement>(null)
  const yearInputRef = useRef<HTMLInputElement>(null)

  const handleChangeDay = (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '')

    if (value === '') {
      setDay('')
      return
    }

    if (value.length === 1 && Number(value) > 3) {
      value = `0${value}`
    }

    if (value.length > 2) value = value.slice(0, 2)

    if (
      (value.length === 1 && Number(value) >= 0 && Number(value) <= 3) ||
      (value.length === 2 && Number(value) >= 1 && Number(value) <= 31)
    ) {
      setDay(value)
      if (value.length === 2) {
        monthInputRef.current?.focus()
      }
    }
  }

  const handleChangeMonth = (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '')

    if (value === '') {
      setMonth('')
      return
    }

    if (value.length === 1 && Number(value) > 1) {
      value = `0${value}`
    }

    if (value.length > 2) value = value.slice(0, 2)

    if (
      (value.length === 1 && Number(value) >= 0 && Number(value) <= 1) ||
      (value.length === 2 && Number(value) >= 1 && Number(value) <= 12)
    ) {
      setMonth(value)
      if (value.length === 2) {
        yearInputRef.current?.focus()
      }
    }
  }

  const handleChangeYear = (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, '')

    if (value.length > 4) value = value.slice(0, 4)

    setYear(value)
  }

  const handleClick = (e: MouseEvent<HTMLInputElement>) => {
    e.currentTarget.select()
  }

  const handleFocusDay = (e: FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select()
    setIsDayFocused(true)
  }

  const handleFocusMonth = (e: FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select()
    setIsMonthFocused(true)
  }

  const handleFocusYear = (e: FocusEvent<HTMLInputElement>) => {
    e.currentTarget.select()
    setIsYearFocused(true)
  }

  const updateDate = () => {
    try {
      const newDate = new Date()

      if (dateValue && !Array.isArray(dateValue)) {
        newDate.setDate(dateValue.getDate())
        newDate.setMonth(dateValue.getMonth())
        newDate.setFullYear(dateValue.getFullYear())
      }

      const dayNum = day ? Number(day) : null
      const monthNum = month ? Number(month) - 1 : null // JS months are 0-11
      const yearNum = year ? Number(year) : null

      if (yearNum !== null && !isNaN(yearNum)) {
        newDate.setFullYear(yearNum)
      }

      if (monthNum !== null && !isNaN(monthNum)) {
        if (monthNum < 0 || monthNum > 11) {
          onError?.('invalid_date')
          return
        }
        newDate.setMonth(monthNum)
      }

      if (dayNum !== null && !isNaN(dayNum)) {
        if (dayNum < 1 || dayNum > 31) {
          onError?.('invalid_date')
          return
        }

        const maxDaysInMonth = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0).getDate()
        if (dayNum > maxDaysInMonth) {
          onError?.('invalid_date')
          return
        }

        newDate.setDate(dayNum)
      }

      const resultingDay = newDate.getDate()
      const resultingMonth = newDate.getMonth()
      const resultingYear = newDate.getFullYear()

      if (
        (dayNum !== null && resultingDay !== dayNum) ||
        (monthNum !== null && resultingMonth !== monthNum) ||
        (yearNum !== null && resultingYear !== yearNum)
      ) {
        onError?.('invalid_date')
        return
      }

      if (dateValue) {
        if (
          !Array.isArray(dateValue) &&
          (dateValue.getDate() !== newDate.getDate() ||
            dateValue.getMonth() !== newDate.getMonth() ||
            dateValue.getFullYear() !== newDate.getFullYear())
        ) {
          selectDate(startOfDay(newDate))
        }
      } else {
        selectDate(startOfDay(newDate))
      }
    } catch (error) {
      console.error('Error updating date:', error)
    }
  }

  const handleBlurDay = () => {
    setIsDayFocused(false)
  }
  const handleBlurMonth = () => {
    setIsMonthFocused(false)
  }
  const handleBlurYear = () => {
    setIsYearFocused(false)
  }

  const isFocused = useMemo(
    () => isDayFocused || isMonthFocused || isYearFocused,
    [isDayFocused, isMonthFocused, isYearFocused],
  )

  useEffect(() => {
    if (dateValue) {
      const d = dateValue as Date
      setDay(d.getDate() > 9 ? String(d.getDate()) : `0${d.getDate()}`)
      setMonth(d.getMonth() + 1 > 9 ? String(d.getMonth() + 1) : `0${d.getMonth() + 1}`)
      setYear(String(d.getFullYear()))
    }
  }, [dateValue])

  const handleClickWrapper = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    modalControls.openPopup()
  }

  useEffect(() => {
    if (!isFocused) {
      updateDate()
    }
  }, [isFocused])

  return (
    <div className={b()} onClick={handleClickWrapper}>
      <input
        ref={ref}
        className={b('input')}
        value={day}
        onChange={handleChangeDay}
        placeholder="__"
        onClick={handleClick}
        onBlur={handleBlurDay}
        onFocus={handleFocusDay}
      />
      <div className={b('divider')}>/</div>
      <input
        ref={monthInputRef}
        className={b('input')}
        value={month}
        onChange={handleChangeMonth}
        placeholder="__"
        onClick={handleClick}
        onBlur={handleBlurMonth}
        onFocus={handleFocusMonth}
      />
      <div className={b('divider')}>/</div>
      <input
        ref={yearInputRef}
        className={b('input', { year: true })}
        value={year}
        onChange={handleChangeYear}
        placeholder="____"
        onClick={handleClick}
        onBlur={handleBlurYear}
        onFocus={handleFocusYear}
      />
    </div>
  )
})

InputDate.displayName = 'InputDate'
